import React, { FC, useEffect } from "react";
import { Loader2 } from "lucide-react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import DownloadButton from "components/DownloadButton";
import TechnicalDrawingDownloadMenu from "components/TechnicalDrawingDownloadMenu";

interface ImagePopupProps {
  image: any;
  /* The url handed to the download controls, when it must differ from the displayed one.
     `image` may be a browser-local `blob:` url (a Tech Pack agent artifact resolved for display),
     and `DownloadButton` forwards its `imageURL` to `logDownload` / `processDownload`, which send it
     to the BE — where a blob url means nothing. Defaults to `image`, so every existing caller is
     unaffected. [DISPLAY_URL_NEVER_OUTBOUND]. */
  downloadUrl?: string | null;
  show: any;
  onClose: () => void;
  isSvgAvailable?: boolean;
  showDownloadMenu?: boolean;
  useeffectreturnFlag?: boolean;
  svgComingSoon?: boolean;
  isTechpackDownload?: boolean;
  viewType?: string;
  details?: Record<string, string | number | null>;
  hideDownload?: boolean;
  /** Show the standard loader in place of the image (e.g. while a full-resolution original is being fetched). */
  isLoading?: boolean;
}

const ImagePopup: FC<ImagePopupProps> = ({
  image,
  downloadUrl,
  show,
  onClose,
  isSvgAvailable,
  showDownloadMenu,
  useeffectreturnFlag = true,
  svgComingSoon,
  isTechpackDownload,
  viewType,
  details,
  hideDownload,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleEscapeKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // The viewer owns this Escape. A dialog underneath (`components/Modal` listens on `window` and skips a
        // `defaultPrevented` event) must not also close — the viewer is a layer on top of it, not the dialog itself.
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    const handleBackdropClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isBackdrop = target.classList.contains("backdrop-blur") || target.classList.contains("bg-black") || target.classList.contains("bg-opacity-70");

      if (isBackdrop) {
        onClose();
      }
    };

    if (show) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleEscapeKeyPress);

      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleBackdropClick);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("keydown", handleEscapeKeyPress);
        document.removeEventListener("mousedown", handleBackdropClick);
      };
    } else {
      // Reset overflow when show becomes false
      if (useeffectreturnFlag !== false) {
        document.body.style.overflow = "auto";
      }
    }

    return () => {
      if (!useeffectreturnFlag) return;
      document.body.style.overflow = "auto";
    };
  }, [show, onClose, useeffectreturnFlag]);

  if (!show || (!image && !isLoading)) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[100000] bg-black bg-opacity-70 flex items-center justify-center py-12"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex h-full w-fit p-5 border-[3px] border-black-200 rounded-lg bg-black-100">
        <div className="flex h-full gap-2.5">
          {isLoading ? (
            <div className="flex-center h-full min-w-[240px] px-10" data-testid="image-popup-loading" role="status" aria-label={t("common:image_popup.loading") as string}>
              <Loader2 size={40} className="animate-spin text-purpleGreen" />
            </div>
          ) : (
            <img src={image} alt={t("common:image_popup.preview_alt")} className="object-contain h-full rounded-[10px] max-w-[80vw] border border-black-200" draggable={false} />
          )}
          {(() => {
            const entries = details ? Object.entries(details).filter(([, value]) => value != null && value !== "") : [];

            if (entries.length === 0) return null;

            return (
              <div className="flex flex-col gap-2 w-[220px] overflow-y-auto no-scrollbar bg-black-200 rounded-[10px] p-4">
                <h4 className="text-white text-bodyMd font-medium mb-1">{t("common:image_popup.details_heading")}</h4>
                {entries.map(([key, value]) => (
                  <div key={key} className="flex flex-col gap-0.5">
                    <span className="text-grey text-bodySm">{key}</span>
                    <span className="text-white text-bodySm">{value}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          {!hideDownload && !isLoading && (
            <div className="h-fit">
              {isTechpackDownload ? (
                <TechnicalDrawingDownloadMenu imageURL={downloadUrl ?? image} isSvgAvailable={isSvgAvailable} isDisplayed={true} svgComingSoon={svgComingSoon} viewType={viewType} />
              ) : (
                <DownloadButton
                  imageURL={downloadUrl ?? image}
                  isSvgAvailable={isSvgAvailable}
                  showDownloadMenu={showDownloadMenu}
                  isDisplayed={true}
                  svgComingSoon={svgComingSoon}
                  isTechpackDownload={isTechpackDownload}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImagePopup;
